import { useContext, useEffect, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCartShopping,
  faHeart,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { FavoriteContext } from "../../Store/Favoris_context";
import { PanierContext } from "../../Store/Panier_context";
import { Button, Spinner } from "react-bootstrap";
import { AuthContext } from "../AuthContext";
import API from "../Authentification/api";
import { getProductImage, getProductImages } from "../../Utils/Cloudinary";
import {
  isWeightSaleProduct,
  buildWeightSaleCartProduct,
  DEFAULT_WEIGHT_SALE_AMOUNT,
} from "../../Utils/weightSale";

const VusProduct = ({ closePopUp, product }) => {
  const { addFavorite, favorites, removeFavorite } =
    useContext(FavoriteContext);
  const { addProductToCart } = useContext(PanierContext);
  const [loading, setLoading] = useState(false);
  const { isLoggedIn } = useContext(AuthContext);

  const isUnavailable = product.disponibility === "non";
  const isWeightSale = isWeightSaleProduct(product);

  // Repli immédiat : l'unique image déjà connue via la carte produit,
  // en attendant que la galerie complète soit chargée (si elle en a plus).
  const [galleryImages, setGalleryImages] = useState(() => [
    getProductImage(product.image),
  ]);
  const [activeImage, setActiveImage] = useState(0);

  // Bloque le scroll en arrière-plan à l'ouverture
  useEffect(() => {
    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = "100%";
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Charge la galerie complète (toutes les images) — la liste de cartes ne
  // fournit qu'une seule image pour rester légère.
  useEffect(() => {
    let cancelled = false;

    const loadGallery = async () => {
      if (!product?.id) return;
      try {
        const res = await API.get(`/products/${product.id}`);
        if (!cancelled) {
          setGalleryImages(getProductImages(res.data.image));
          setActiveImage(0);
        }
      } catch (error) {
        if (process.env.NODE_ENV !== "production") {
          console.error("Erreur chargement galerie produit:", error);
        }
      }
    };

    loadGallery();
    return () => {
      cancelled = true;
    };
  }, [product?.id]);

  const isFavorite = favorites?.some((fav) => fav.product_id === product.id);

  const toggleFavorite = useCallback(() => {
    if (isFavorite) {
      const fav = favorites.find((f) => f.product_id === product.id);
      removeFavorite(fav.id);
    } else {
      addFavorite(product.id);
    }
  }, [isFavorite, favorites, product.id, addFavorite, removeFavorite]);

  const formatPrice = (price) => {
    const numericValue = typeof price === "string" ? parseFloat(price) : price;

    return new Intl.NumberFormat("fr-FR", {
      style: "decimal",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numericValue);
  };

  // Enregistrement vue récente
  useEffect(() => {
    const token = localStorage.getItem("token");

    if (token && product?.id) {
      const registerView = async () => {
        try {
          await API.post("/recent-views", { product_id: product.id });
        } catch (error) {
          if (process.env.NODE_ENV !== "production") {
            console.error(
              "Erreur ajout vue récente:",
              error.response.data.message,
            );
          }
        }
      };

      registerView();
    }
  }, [product]);

  const handleAddToCart = () => {
    if (isUnavailable) return;

    if (isWeightSale) {
      addProductToCart(buildWeightSaleCartProduct(product));
      return;
    }

    addProductToCart(product);
  };

  return (
    <div className="popup-overlay">
      <div className="popup shadow-sm p-3 rounded-3">
        <button
          onClick={closePopUp}
          className="bouton-close text-xxl"
          style={{ color: "red" }}
        >
          ✕
        </button>
        <div className="row">
          <div
            className={`col-lg-6 col-12 me-2 ${isUnavailable ? "nm-dispo-off" : ""}`}
          >
            <div className="nm-gallery-wrapper">
              <div className="nm-gallery-main">
                <img
                  src={galleryImages[activeImage]}
                  alt={product.name}
                  className="image_vus_product"
                />
              </div>

              {galleryImages.length > 1 && (
                <div className="nm-gallery-thumbs">
                  {galleryImages.map((src, i) => (
                    <button
                      type="button"
                      key={i}
                      className={`nm-gallery-thumb ${i === activeImage ? "is-active" : ""}`}
                      onClick={() => setActiveImage(i)}
                      aria-label={`Voir l'image ${i + 1}`}
                    >
                      <img src={src} alt={`${product.name} ${i + 1}`} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="col-lg col-12">
            <h5
              className="name_entreprise_dashboard mt-4"
              style={{ textTransform: "none" }}
            >
              {product.name}
            </h5>
            <div className="product-price-block">
              <span className="price-label">
                {isWeightSale ? "Prix au kilo" : "Prix"}
              </span>
              <div className="price-main">
                <span className="price-amount">
                  {formatPrice(product.price || product.new_price)}
                </span>
                <span className="price-currency">
                  FCFA{isWeightSale ? "/kg" : ""}
                </span>
              </div>
              <span className="price-tax">
                Livraison calculée à la commande
              </span>
            </div>
            <p className="texte_brut">
              {product.description
                ? product.description
                : "Description non disponible pour l'instant"}
            </p>
            {isUnavailable && (
              <p className="nm-dispo-label mb-2">Non disponible</p>
            )}
          </div>

          <div className="mt-2">
            <div className="d-flex flex-column flex-md-row justify-content-center gap-3">
              <Button
                onClick={handleAddToCart}
                disabled={isUnavailable}
                style={
                  isUnavailable
                    ? { backgroundColor: "#adb5bd", borderColor: "#adb5bd" }
                    : undefined
                }
              >
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <span>
                    <span className="petit_titre">
                      {isUnavailable ? "Indisponible" : "Ajouter au panier"}
                    </span>
                    {!isUnavailable && (
                      <FontAwesomeIcon icon={faCartShopping} className="ms-2" />
                    )}
                  </span>
                )}
              </Button>
              <Button
                onClick={toggleFavorite}
                style={{ backgroundColor: isFavorite ? "red" : "#FA7F1B" }}
                className="border-0"
                disabled={!isLoggedIn}
              >
                <span className="petit_titre">
                  {isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
                </span>

                <FontAwesomeIcon
                  icon={faHeart}
                  className="ms-2"
                  style={{
                    cursor: "pointer",
                    color: "white",
                    transition: "0.2s",
                  }}
                />
              </Button>
            </div>

            {isWeightSale && !isUnavailable && (
              <p className="nm-weight-warning">
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="me-1"
                />
                Produit vendu au poids —{" "}
                {formatPrice(DEFAULT_WEIGHT_SALE_AMOUNT)} FCFA ajoutés par
                défaut. Ajustez le montant ou le poids exact dans votre panier.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VusProduct;
